import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Spinner, Center, VStack, Text, useDisclosure, useToast, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  Textarea, Button, SimpleGrid, Image, Heading, Badge,
} from '@chakra-ui/react';
import { useCourseData } from '../hooks/useCourseData';
import { useAppData } from '../store/dataStore';
import CoursePricing from '../components/course/CoursePricing';
import { TRANSFORM_STACK, TRANSFORM_STACK_GROUPS } from '../data/transformStack';

// '9,999' / '₹9,999' -> 9999
const parseAmount = (str) => {
  const n = parseInt(String(str).replace(/[^\d]/g, ''), 10);
  return Number.isNaN(n) ? undefined : n;
};

// Per-course hero/transformation imagery. Add an entry here for every
// course that gets a /transform/:courseId page.
const TRANSFORM_IMAGES = {
  6: {
    before: 'https://images.ojasamirai.com/ojasa/slides/3001/before.png',
    after: 'https://images.ojasamirai.com/ojasa/slides/3001/after.png',
  },
};

const COLORS = {
  ink: '#14171C',
  cream: '#FAF8F3',
  creamSoft: '#F3EEE4',
  orange: '#D9772E',
  orangeDark: '#B8611F',
  orangeTint: '#FBEADD',
  textBody: '#3C4149',
  textMuted: '#6B7078',
};

const styles = {
  page: {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    color: COLORS.ink,
    background: COLORS.cream,
    fontFamily: "'Inter', sans-serif",
  },
  section: (extra) => ({
    width: '100%',
    boxSizing: 'border-box',
    padding: '88px 64px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...extra,
  }),
  eyebrow: (color = COLORS.orange) => ({
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color,
  }),
  h2: {
    margin: 0,
    fontFamily: "'Merriweather', serif",
    fontWeight: 700,
    lineHeight: 1.25,
  },
  cardBase: {
    background: '#FFFFFF',
    border: '1px solid rgba(20,23,28,0.08)',
    borderRadius: '16px',
  },
};

const responsiveCss = `
  .tf-cta:hover { background: ${COLORS.orangeDark} !important; }
  .tf-cta-ghost:hover { border-color: ${COLORS.ink} !important; }
  .tf-help-btn:hover { border-color: ${COLORS.orangeDark} !important; color: ${COLORS.orangeDark} !important; }
  .tf-pillar { transition: transform 0.15s ease, box-shadow 0.15s ease; }
  .tf-pillar:hover { transform: translateY(-4px); box-shadow: 0 12px 28px rgba(20,23,28,0.10); }
  .tf-grid-2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; width: 100%; }
  .tf-grid-3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; width: 100%; }
  .tf-hero-grid { display: grid; grid-template-columns: 0.78fr 1.22fr; gap: 28px; align-items: start; width: 100%; }
  @media (max-width: 900px) {
    .tf-grid-2, .tf-grid-3 { grid-template-columns: 1fr; }
    .tf-hero-grid { grid-template-columns: 1fr; }
  }
`;

// The 26-step learner journey (see docs/courses/_project/journey.md),
// grouped into five narrative phases instead of a flat list of badges.
const JOURNEY_PHASES = [
  {
    phase: 'Phase 1',
    title: 'Think like an engineer before you touch code',
    body: "You don't start by coding — you start the way a real team does: picking a project and writing the requirement doc that scopes what's in and out. You get oriented with the IDE and the AI chat, learn how Claude Code, MCP and Skills actually fit together, then get the full breadth of the stack and the architecture reasoning behind it — before a single line of implementation.",
    skills: ['Requirements management', 'Agentic AI tooling', 'Full-stack breadth', 'Architecture decisions'],
  },
  {
    phase: 'Phase 2',
    title: 'Build for real, with an AI agent as your partner',
    body: 'This is where most courses start with a template and stop. You scaffold a real repo, design the API and data model, build the UI and backend services, and go through actual build cycles with an AI agent — planning the work, reading every PR it opens, catching what it gets wrong, and knowing exactly when to take the wheel yourself.',
    skills: ['Scaffolding & tooling', 'API & data model design', 'UI & backend services', 'Build cycle with AI', 'Reviewing AI-written code', 'GitHub workflow with AI'],
  },
  {
    phase: 'Phase 3',
    title: 'Make it solid enough to trust',
    body: "Code that runs on your machine isn't done. You write automated tests that actually catch regressions, profile and fix real performance bottlenecks, and close the security gaps most side projects never think about — the difference between a demo and something you'd hand to a stranger.",
    skills: ['Automated testing', 'Performance profiling', 'Security basics'],
  },
  {
    phase: 'Phase 4',
    title: 'Ship it, and keep it alive',
    body: 'You deploy to a real URL, wire up a CI/CD pipeline, and ship a patch release without breaking what\'s already live. Then you do what almost no course covers: set up observation and analytics so you can see what\'s actually happening in production, and practice handling an incident and rolling back when something breaks.',
    skills: ['Deployment', 'CI/CD pipeline', 'Patch builds', 'Observation & monitoring', 'Analytics', 'Incident handling & rollback'],
  },
  {
    phase: 'Phase 5',
    title: 'Own the story, not just the repo',
    body: 'You document the project and hand it off the way a real team would, then walk the complete lifecycle end to end in a final capstone review — from the original requirement to a shipped, monitored product. You leave with a certificate, and more importantly, a story you can actually tell in an interview.',
    skills: ['Documentation & handoff', 'Complete lifecycle review', 'Certificate & portfolio narrative'],
  },
];

const FEATURES_FALLBACK = [
  { icon: '🎓', title: 'Advanced topics, not just a project brief', description: 'Architecture trade-offs, agent orchestration, observability, security — the topics a junior dev usually only sees a year into a job.' },
  { icon: '💻', title: 'A real IDE, not a browser toy', description: 'Every exercise runs in the VS Code extension — the same editor, terminal and workflow used on the job.' },
  { icon: '🤝', title: 'Agentic AI as a teammate, not a cheat code', description: 'Plan with an agent, review what it writes, catch mistakes, know when to take the wheel.' },
  { icon: '🔄', title: 'The whole lifecycle, one project', description: 'Requirements → design → implementation → review → deployment, on one growing capstone.' },
  { icon: '🧑‍🏫', title: 'Real handholding, not a group Q&A', description: '4 dedicated 1:1 mentor sessions reviewing your own project, plus a 30-minute live class every day.' },
  { icon: '📋', title: 'Choice of project, day one', description: 'Pick from a curated project list, or bring a personal idea *' },
];

const OUTCOMES_FALLBACK = [
  { title: 'A shipped, deployed capstone project', description: 'Built end-to-end, from a written requirement to a working URL.' },
  { title: 'A real commit and PR history', description: 'The kind interviewers actually read, not a single upload.' },
  { title: 'Fluency with agentic AI workflows', description: 'Planning, reviewing, and correcting AI-written code.' },
  { title: 'A mentor who reviewed the actual code', description: 'Four dedicated 1:1 sessions on your own project.' },
];

const AUDIENCE_FALLBACK = {
  idealFor: [
    'Can already write basic code and wants one real, deployed product',
    'Wants AI as part of the actual workflow, not a novelty',
    'Wants to close the gap between watching and shipping',
  ],
  notIdealFor: [
    'Wants to copy-paste AI output without understanding it',
    'Wants a passive, video-only course with nothing to show for it',
  ],
};

// Local pricing fallback, used until this pricing is live in Firebase
// for course 6 — keep numbers in sync with docs/courses/_project/benefits.md
const PRICING_FALLBACK = {
  currency: 'INR',
  mrp: 14000,
  price: 9999,
  badge: 'Cohort starts today',
  discountText: '+18% GST extra · bring your own Claude Code subscription · + personal project idea charges',
  emi: { available: false },
  valueBreakdown: [
    { item: 'Daily live classes (30 min × 5 weeks)', value: 10000, description: 'Every day of the program, short enough to never skip' },
    { item: '1:1 mentorship (4 dedicated sessions)', value: 4000, description: 'Dedicated one-on-one time on your own project' },
    { item: 'Project', value: 10000, description: 'Your curated capstone project, scoped and ready to build' },
    { item: 'Varsity', value: 2000, description: 'Ongoing access to our knowledge base' },
  ],
  // Kept for reference; the actual total shown is computed live from valueBreakdown above.
  totalValue: 26000,
  includesList: [
    'Daily 30-minute live class, every day for 5 weeks',
    '4× 1:1 mentor meetings on your own project',
    'Choice of curated project from the list',
    'Varsity access — our knowledge base, ongoing',
    '100,000 AI credits for your agentic workflow',
    '50,000 cloud credits (DB, APIs & hosting)',
    'Completion certificate backed by a shipped project',
    'Real commit and PR history under your name',
    '₹8,000 off our AI-Engineer course when you continue after this',
  ],
  enrollmentUrl: '/enroll?courseId={courseId}',
  previewUrl: '/webinar?courseId={courseId}',
};

const OFFER = {
  title: 'Keep going after this: ₹8,000 off the AI-Engineer course',
  description: "Finish Full Cycle Pro and you're ready for the next step. Enroll in our AI-Engineer course afterward and take ₹8,000 off, automatically.",
  cta: 'See the AI-Engineer course',
  href: 'https://ojasamirai.com/beginner-track',
};

// AI-Engineer-specific topics covered by the free self-paced Bridge Course.
const BRIDGE_TOPICS = [
  { topic: 'LLM fundamentals', significance: "How large language models actually generate text, and where they fail. Without this, debugging odd AI behavior turns into guesswork." },
  { topic: 'Prompt engineering', significance: 'Structuring instructions so a model reliably does what you want. The single most transferable AI-Engineer skill, used in almost every AI product.' },
  { topic: 'RAG', significance: "Grounding an LLM's answers in your own data instead of just its training data. The default architecture behind most real-world AI products." },
  { topic: 'Embeddings / vector databases', significance: 'How semantic search actually works under the hood — powers RAG, recommendations, and de-duplication.' },
  { topic: 'LLM evaluation', significance: 'Measuring whether an AI feature actually works, beyond "it looked fine when I tried it." Skipping this is how regressions ship silently.' },
  { topic: 'Tool calling / structured output', significance: 'Getting an LLM to reliably call functions and return usable JSON instead of free text — the backbone of every agent and AI integration.' },
  { topic: 'AI agents architecture', significance: 'Designing how an AI system plans, uses tools, and hands off tasks. Different from using an agentic coding tool — this is building one.' },
  { topic: 'LLM orchestration', significance: "Coordinating multiple models, calls, and steps into one reliable pipeline. Needed the moment a single prompt isn't enough." },
  { topic: 'Model selection / inference', significance: "Choosing the right model for cost, latency, and quality, and running it efficiently. Directly affects a product's speed and the bill." },
  { topic: 'Fine-tuning', significance: 'Adapting a model to a specific task by training it further — useful once prompting alone hits its limits.' },
  { topic: 'AI safety / guardrails', significance: 'Preventing prompt injection, data leaks, and harmful outputs before they reach a user. Increasingly a compliance requirement, not a nice-to-have.' },
  { topic: 'AI application cost / latency optimization', significance: 'Keeping an AI feature fast and affordable at real usage volumes — the difference between a demo and a product that survives production traffic.' },
  { topic: 'AI-specific system design', significance: 'Designing the architecture around an LLM: caching, fallbacks, retries, observability. Exactly what interviewers probe for in AI-Engineer system-design rounds.' },
];

const WhatsAppIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.39 1.26 4.81L2 22l5.4-1.36a9.9 9.9 0 0 0 4.64 1.16h.01c5.46 0 9.9-4.45 9.9-9.9C21.95 6.45 17.5 2 12.04 2zm5.8 14.1c-.24.68-1.4 1.3-1.93 1.38-.5.08-1.12.11-1.8-.11-.41-.13-.94-.3-1.62-.6-2.85-1.23-4.71-4.1-4.85-4.29-.14-.19-1.15-1.53-1.15-2.92 0-1.39.73-2.07.99-2.35.26-.28.56-.35.75-.35.19 0 .38 0 .54.01.17.01.4-.07.63.48.24.57.81 1.97.88 2.11.07.14.11.3.02.49-.09.19-.14.3-.28.46-.14.16-.29.36-.42.48-.14.13-.28.28-.12.55.16.28.72 1.19 1.55 1.93 1.06.95 1.96 1.24 2.24 1.38.28.14.44.12.6-.07.16-.19.68-.79.86-1.07.18-.28.36-.23.6-.14.24.09 1.55.73 1.82.86.27.14.45.2.51.31.06.12.06.68-.18 1.36z"/>
  </svg>
);

const CheckSvg = ({ color = COLORS.orange }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const ToolsSvg = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
  </svg>
);

const COPY_PROMPT_HELP = "Copies a ready-made prompt that summarizes this program — what it covers, what you walk away with, and the price. Paste it into ChatGPT, Claude, or any AI assistant to get an independent, honest read on whether it actually fits your goals.";

const InfoButton = ({ label }) => (
  <Tooltip label={label} hasArrow placement="top" fontSize="sm" bg={COLORS.ink} color={COLORS.cream} borderRadius="md" px={3} py={2} maxW="280px">
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '22px', height: '22px', borderRadius: '50%',
        border: `1.5px solid ${COLORS.textMuted}`, color: COLORS.textMuted,
        fontSize: '12px', fontWeight: 700, fontStyle: 'italic', fontFamily: 'Georgia, serif',
        cursor: 'help', flexShrink: 0,
      }}
    >
      i
    </span>
  </Tooltip>
);

const scrollToPricing = () => {
  const el = document.querySelector('[data-pricing]');
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

// "How does this help?" ghost button, shown under every major section.
const HelpButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="tf-help-btn"
    style={{
      marginTop: '28px',
      background: 'transparent',
      border: '1.5px solid rgba(20,23,28,0.18)',
      color: COLORS.textMuted,
      borderRadius: '999px',
      padding: '9px 20px',
      fontSize: '13.5px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.15s ease',
    }}
  >
    How does this help?
  </button>
);

const TransformLanding = () => {
  const { courseId: rawCourseId } = useParams();
  const courseId = (rawCourseId || '6').split('-')[0];
  const { courseData, loading } = useCourseData(courseId);
  const { appData } = useAppData();
  const toast = useToast();

  const stackModal = useDisclosure();
  const bridgeModal = useDisclosure();
  const [promptModal, setPromptModal] = useState({ isOpen: false, title: '', prompt: '' });

  // dataStore.js's courseN entry is the authoritative source for start date / price / MRP,
  // even when Firebase already has a live record for this course — that keeps this page
  // and the /enroll checkout flow (which also reads dataStore.js) always in sync on the
  // numbers that change most often, without needing a Firebase write for a price update.
  const courseMeta = appData?.[`course${courseId}`];

  const images = TRANSFORM_IMAGES[courseId] || TRANSFORM_IMAGES['6'];
  const hero = courseData?.hero;
  const features = courseData?.features?.features?.length ? courseData.features.features : FEATURES_FALLBACK;
  const outcomes = courseData?.outcomes?.outcomes?.length ? courseData.outcomes.outcomes : OUTCOMES_FALLBACK;
  const audienceCheck = courseData?.audienceCheck || AUDIENCE_FALLBACK;
  const trustBadges = courseData?.trustBadges?.badges || [];
  const basePricing = courseData?.pricing || PRICING_FALLBACK;
  // totalValue is always the actual sum of valueBreakdown — never a separately
  // maintained number — so the "Value Breakdown" modal can never drift out of
  // sync with the line items it's adding up.
  const valueBreakdownSum = (basePricing.valueBreakdown || []).reduce((sum, item) => sum + (item.value || 0), 0);
  const pricing = {
    ...basePricing,
    price: parseAmount(courseMeta?.price) ?? basePricing.price,
    mrp: parseAmount(courseMeta?.mrp) ?? basePricing.mrp,
    totalValue: valueBreakdownSum || basePricing.totalValue,
  };
  const settings = courseData?.settings;
  const startDate = courseMeta?.startDate || settings?.courseStartDate;

  const heroDescription = hero?.description || "Learn to build software the way it's actually built now — inside a real IDE, with a real agentic AI workflow, shipping a real project from requirement to deployment, with a mentor beside you the entire way.";

  const openPrompt = (title, bodyLines) => {
    const prompt = [
      "I am looking to get into the role of the likes of 'AI Engineer'. Does this help me?",
      '',
      ...bodyLines,
      '',
      'Based on this, give me an honest read on whether it actually moves me toward being job-ready, and what (if anything) is missing.',
    ].join('\n');
    setPromptModal({ isOpen: true, title, prompt });
  };

  const openFullProgramPrompt = () => {
    openPrompt('Copy prompt: the full program', [
      `Program: ${hero?.title || 'Full Cycle Pro'} — ${hero?.subtitle || 'Master Concepts → Build with AI → Ship to Production'}`,
      heroDescription,
      '',
      "It's not just building a project — it covers advanced topics with real 1:1 mentor handholding:",
      ...JOURNEY_PHASES.map((p, i) => `${i + 1}. ${p.title}: ${p.body}`),
      '',
      'What I walk away with:',
      ...outcomes.map((o) => `- ${o.title}: ${o.description}`),
      '',
      'Also included free with enrollment: a self-paced Bridge Course covering AI-Engineer-specific topics the main program doesn\'t teach directly:',
      ...BRIDGE_TOPICS.map((t) => `- ${t.topic}: ${t.significance}`),
      '',
      `Price: ₹${pricing.price} + 18% GST, bring your own Claude Code subscription. Includes 100,000 AI credits, 50,000 cloud credits, and ₹8,000 off a follow-on AI-Engineer course.`,
    ]);
  };

  const openSectionPrompt = (title, lines) => openPrompt(title, [
    `Section: "${title}" from the ${hero?.title || 'Full Cycle Pro'} program.`,
    '',
    ...lines,
  ]);

  const copyPrompt = () => {
    navigator.clipboard?.writeText(promptModal.prompt).then(() => {
      toast({ title: 'Prompt copied', status: 'success', duration: 2000, isClosable: true });
    });
  };

  return (
    <Box sx={{ '*': { boxSizing: 'border-box' } }}>
      <style>{responsiveCss}</style>
      <div style={styles.page}>

        {/* TOP BAR */}
        <div style={{ width: '100%', padding: '20px 64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(20,23,28,0.08)' }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center' }}>
            <img src="https://images.ojasamirai.com/ojasa/common/logo.png" alt="Ojasa Mirai" style={{ height: '38px', objectFit: 'contain' }} />
          </a>
          <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToPricing(); }} className="tf-cta" style={{ background: COLORS.orange, color: COLORS.cream, padding: '10px 22px', borderRadius: '8px', fontWeight: 600, fontSize: '15px' }}>
            Enroll Now
          </a>
        </div>

        {/* HERO */}
        <div style={styles.section({ paddingTop: '80px', paddingBottom: '72px', alignItems: 'stretch', background: `linear-gradient(180deg, #FDFBF7 0%, ${COLORS.cream} 100%)` })}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', border: '1px solid rgba(20,23,28,0.14)', borderRadius: '999px', fontSize: '13px', fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase', color: COLORS.orangeDark, width: 'fit-content' }}>
              {hero?.badge || '300+ Developers Trained'}
            </div>
            <h1 style={{ margin: 0, fontFamily: "'Merriweather', serif", fontSize: '48px', lineHeight: 1.15, fontWeight: 700, letterSpacing: '-0.01em' }}>
              {hero?.title || 'Full Cycle Pro'}
            </h1>
            <div style={{ fontSize: '14px', fontWeight: 600, color: COLORS.orangeDark, letterSpacing: '0.01em' }}>
              {hero?.subtitle || 'Master Concepts → Build with AI → Ship to Production'}
            </div>
          </div>

          <div className="tf-hero-grid">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <p style={{ margin: 0, fontSize: '18px', lineHeight: 1.6, color: COLORS.textBody }}>
                {heroDescription}
              </p>
              <p style={{ margin: 0, fontSize: '15.5px', lineHeight: 1.7, color: COLORS.textBody }}>
                <b>This is far more than "build a project."</b> You'll go through advanced topics most bootcamps skip entirely — architecture trade-offs, agent orchestration, security, observability — with a mentor holding your hand through every one of them, not a forum you post questions into.
              </p>
            </div>
            <div>
              <img
                src={images.before}
                alt="Where most self-taught developers get stuck"
                style={{ width: '100%', borderRadius: '18px', boxShadow: '0 24px 60px rgba(20,23,28,0.18)',  border: '1px solid rgba(20,23,28,0.08)' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '28px' }}>
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <InfoButton label={COPY_PROMPT_HELP} />
                <button onClick={openFullProgramPrompt} className="tf-cta" style={{ background: COLORS.orange, color: COLORS.cream, padding: '16px 32px', borderRadius: '10px', fontSize: '16px', fontWeight: 700, border: 'none', cursor: 'pointer' }}>
                  Copy Prompt
                </button>
              </div>
              <a
                href={hero?.secondaryButton?.href || 'https://wa.me/919900309000?text=Hi%20I%20have%20questions%20about%20the%20Full%20Cycle%20Pro%20course'}
                target="_blank"
                rel="noreferrer"
                className="tf-cta-ghost"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', border: '1.5px solid rgba(20,23,28,0.22)', padding: '16px 32px', borderRadius: '10px', fontSize: '16px', fontWeight: 600, color: '#25D366' }}
              >
                <WhatsAppIcon />
                <span style={{ color: COLORS.ink }}>Chat with mentor</span>
              </a>
            </div>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '13.5px', color: COLORS.textMuted }}>
              {(hero?.trustElements || [
                { text: 'Real Projects, Real Repos' },
                { text: 'Daily Live Mentor Class' },
                { text: 'Deployed Capstone' },
              ]).map((t, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckSvg />
                  {t.text}
                </div>
              ))}
            </div>
            <button
              onClick={stackModal.onOpen}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px', width: 'fit-content',
                background: COLORS.ink, color: COLORS.cream, border: 'none', borderRadius: '10px',
                padding: '14px 24px', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
              }}
            >
              <ToolsSvg />
              What tools you will work on
            </button>
          </div>
        </div>

        {/* BANNER */}
        <div style={{ width: '100%', padding: '16px 64px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', background: COLORS.orange, color: '#FFFFFF', fontSize: '14.5px', fontWeight: 600, textAlign: 'center' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#FFFFFF', display: 'inline-block' }}></span>
          {settings?.isLive ? 'Doors are open — the next cohort is filling up. Reserve your seat before it closes.' : 'Applications are open for the next cohort.'}
        </div>

        {/* THE JOURNEY — why this is a genuinely different program */}
        <div style={styles.section({})}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '16px', marginBottom: '48px' }}>
            <div style={styles.eyebrow()}>The journey</div>
            <h2 style={{ ...styles.h2, maxWidth: '720px', fontSize: '34px' }}>One project, five phases, the entire job</h2>
            <p style={{ margin: 0, maxWidth: '700px', fontSize: '16.5px', lineHeight: 1.7, color: COLORS.textBody }}>
              This isn't 26 disconnected topics bolted together. It's one growing capstone, and every phase below is a real stage that project — and you — move through, in order, with a mentor beside you at each one.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0', width: '100%', maxWidth: '900px' }}>
            {JOURNEY_PHASES.map((p, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: '28px',
                  padding: '30px 0',
                  borderBottom: i < JOURNEY_PHASES.length - 1 ? '1px solid rgba(20,23,28,0.08)' : 'none',
                  textAlign: 'left',
                }}
              >
                <div style={{ flexShrink: 0, width: '96px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.orangeDark }}>
                    {p.phase}
                  </div>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: COLORS.orangeTint, color: COLORS.orangeDark, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '14px', marginTop: '8px' }}>
                    {i + 1}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ fontFamily: "'Merriweather', serif", fontSize: '21px', fontWeight: 700 }}>{p.title}</div>
                  <div style={{ fontSize: '15.5px', lineHeight: 1.75, color: COLORS.textBody }}>{p.body}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                    {p.skills.map((s, si) => (
                      <div key={si} style={{ fontSize: '12.5px', fontWeight: 600, color: COLORS.orangeDark, background: COLORS.orangeTint, borderRadius: '999px', padding: '5px 12px' }}>
                        {s}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <HelpButton onClick={() => openSectionPrompt('The journey', JOURNEY_PHASES.map((p, i) => `${i + 1}. ${p.title}: ${p.body}`))} />
        </div>

        {/* WHAT WEEK 5 LOOKS LIKE */}
        <div style={styles.section({ paddingTop: '0' })}>
          <img
            src={images.after}
            alt="A deployed, monitored product built during the program"
            style={{ width: '100%', maxWidth: '980px', borderRadius: '18px', boxShadow: '0 24px 60px rgba(20,23,28,0.14)', border: '1px solid rgba(20,23,28,0.08)' }}
          />
          <p style={{ margin: '20px 0 0', maxWidth: '640px', textAlign: 'center', fontSize: '15px', color: COLORS.textBody }}>
            A deployed, monitored product — and the instinct to plan, review, and fix with an AI agent beside you.
          </p>
        </div>

        {/* THE GAP */}
        <div style={styles.section({ background: COLORS.creamSoft, gap: '22px', textAlign: 'center' })}>
          <div style={styles.eyebrow()}>The problem with most courses</div>
          <h2 style={{ ...styles.h2, maxWidth: '760px', fontSize: '32px' }}>
            They stop the moment your code runs. The real job has twenty more steps after that.
          </h2>
          <p style={{ margin: 0, maxWidth: '720px', fontSize: '16.5px', lineHeight: 1.75, color: COLORS.textBody }}>
            Watch a video, pass a quiz, get a badge — that proves you sat through something, not that you can do the job. The job doesn't start with writing code, and it definitely doesn't end when it runs on your laptop. It starts with a requirement you have to scope and an architecture you have to decide on. It continues after your first commit — getting a PR reviewed, catching what an AI agent got subtly wrong, writing tests that actually catch regressions, closing security gaps, shipping through a CI/CD pipeline, watching what breaks once real people use it, and fixing it live without taking everything down.
          </p>
          <p style={{ margin: 0, maxWidth: '720px', fontSize: '16.5px', lineHeight: 1.75, color: COLORS.ink, fontWeight: 600 }}>
            Full Cycle Pro is the one program that walks you through all of it, end to end, on one real project, with a mentor's hand on your shoulder at every stage — because that's the actual job, not the part of it that fits neatly into a 5-minute video.
          </p>
          <HelpButton onClick={() => openSectionPrompt('The problem with most courses', [
            'Most courses stop once the code runs on a laptop.',
            'Full Cycle Pro instead covers requirements, architecture, reviewing AI-written code, testing, security, CI/CD, production monitoring, and incident handling — with 1:1 mentor handholding at each stage.',
          ])} />
        </div>

        {/* FEATURES / HIGHLIGHTS */}
        <div style={styles.section({})}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '16px', marginBottom: '48px' }}>
            <div style={styles.eyebrow()}>{courseData?.features?.title ? 'What makes it powerful' : 'Program highlights'}</div>
            <h2 style={{ ...styles.h2, maxWidth: '680px', fontSize: '34px' }}>
              {courseData?.features?.subtitle || 'Advanced topics, real mentorship, one growing capstone'}
            </h2>
          </div>
          <div className="tf-grid-3">
            {features.map((f, i) => (
              <div key={i} className="tf-pillar" style={{ ...styles.cardBase, padding: '32px 26px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: COLORS.orangeTint, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                  {f.icon}
                </div>
                <div style={{ fontFamily: "'Merriweather', serif", fontSize: '18px', fontWeight: 700 }}>{f.title}</div>
                <div style={{ fontSize: '14.5px', lineHeight: 1.65, color: COLORS.textBody }}>{f.description}</div>
              </div>
            ))}
          </div>
          <button
            onClick={stackModal.onOpen}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '10px', width: 'fit-content',
              background: COLORS.ink, color: COLORS.cream, border: 'none', borderRadius: '10px',
              padding: '16px 28px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', marginTop: '40px',
            }}
          >
            <ToolsSvg />
            What tools you will work on
          </button>
          <HelpButton onClick={() => openSectionPrompt('Program highlights', features.map((f) => `${f.title}: ${f.description}`))} />
        </div>

        {/* THE 5 WEEKS */}
        <div style={styles.section({ background: COLORS.ink, color: COLORS.cream, gap: '44px' })}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '16px' }}>
            <div style={styles.eyebrow('#E8955C')}>Five weeks, one arc</div>
            <h2 style={{ ...styles.h2, maxWidth: '640px', fontSize: '32px', color: COLORS.cream }}>
              {courseData?.curriculum?.title || 'Breadth → Depth → Practice → Handholding → Full Lifecycle'}
            </h2>
          </div>
          <div className="tf-grid-3" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', maxWidth: '1120px' }}>
            {[
              { w: 'WEEK 1', t: 'Breadth', d: 'The full stack, mapped — frontend, backend, data, and where agentic AI fits into each.' },
              { w: 'WEEK 2', t: 'Depth', d: 'Go deep on your stack of choice — real architecture decisions, not toy examples.' },
              { w: 'WEEK 3', t: 'Practice', d: 'Build for real, inside the IDE, with the AI agent as your working partner.' },
              { w: 'WEEK 4', t: 'Handholding', d: 'Mentor review on your actual repo — stuck points resolved, not glossed over.' },
              { w: 'WEEK 5', t: 'Full Lifecycle', d: 'Deploy, ship a patch release, then wire up observation and analytics.', highlight: true },
            ].map((wk, i) => (
              <div key={i} style={{
                background: wk.highlight ? 'rgba(217,119,46,0.14)' : 'rgba(250,248,243,0.05)',
                border: `1px solid ${wk.highlight ? COLORS.orange : 'rgba(250,248,243,0.12)'}`,
                borderRadius: '14px', padding: '22px 16px', display: 'flex', flexDirection: 'column', gap: '10px',
              }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#E8955C' }}>{wk.w}</div>
                <div style={{ fontFamily: "'Merriweather', serif", fontSize: '18px', fontWeight: 700 }}>{wk.t}</div>
                <div style={{ fontSize: '13px', lineHeight: 1.6, color: 'rgba(250,248,243,0.8)' }}>{wk.d}</div>
              </div>
            ))}
          </div>
          <HelpButton onClick={() => openSectionPrompt('Five weeks, one arc', [
            'Week 1 Breadth, Week 2 Depth, Week 3 Practice, Week 4 Handholding, Week 5 Full Lifecycle.',
            'Each week builds on the last, on one growing capstone, with a mentor reviewing progress along the way.',
          ])} />
        </div>

        {/* OUTCOMES */}
        <div style={styles.section({})}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '16px', marginBottom: '48px' }}>
            <div style={styles.eyebrow()}>What you walk away with</div>
            <h2 style={{ ...styles.h2, maxWidth: '640px', fontSize: '34px' }}>Not a certificate. A portfolio.</h2>
            <p style={{ margin: 0, maxWidth: '600px', fontSize: '16px', lineHeight: 1.7, color: COLORS.textBody }}>
              Every deliverable lives in a real GitHub repo under your name — the thing you actually show in an interview.
            </p>
          </div>
          <div className="tf-grid-2" style={{ maxWidth: '920px' }}>
            {outcomes.map((o, i) => (
              <div key={i} style={{ display: 'flex', gap: '14px', padding: '18px 0', borderBottom: '1px solid rgba(20,23,28,0.08)' }}>
                <CheckSvg />
                <div style={{ fontSize: '15px', lineHeight: 1.55 }}>
                  <b>{o.title}</b> — <span style={{ color: COLORS.textBody }}>{o.description}</span>
                </div>
              </div>
            ))}
          </div>
          <HelpButton onClick={() => openSectionPrompt('What you walk away with', outcomes.map((o) => `${o.title}: ${o.description}`))} />
        </div>

        {/* WHO IT'S FOR */}
        <div style={styles.section({ background: COLORS.creamSoft, gap: '32px' })}>
          <h2 style={{ ...styles.h2, fontSize: '30px', textAlign: 'center' }}>Is this for you?</h2>
          <div className="tf-grid-2" style={{ maxWidth: '900px' }}>
            <div style={{ background: '#FFFFFF', borderRadius: '14px', padding: '26px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#2E7D5B' }}>This is for you if</div>
              {audienceCheck.idealFor.map((t, i) => (
                <div key={i} style={{ fontSize: '14.5px', lineHeight: 1.6, color: COLORS.textBody }}>• {t}</div>
              ))}
            </div>
            <div style={{ background: '#FFFFFF', borderRadius: '14px', padding: '26px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: COLORS.orangeDark }}>This isn't for you if</div>
              {audienceCheck.notIdealFor.map((t, i) => (
                <div key={i} style={{ fontSize: '14.5px', lineHeight: 1.6, color: COLORS.textBody }}>• {t}</div>
              ))}
            </div>
          </div>
          <HelpButton onClick={() => openSectionPrompt('Is this for you?', [
            ...audienceCheck.idealFor.map((t) => `Good fit: ${t}`),
            ...audienceCheck.notIdealFor.map((t) => `Not a fit: ${t}`),
          ])} />
        </div>

        {/* TRUST BADGES */}
        {trustBadges.length > 0 && (
          <div style={{ width: '100%', padding: '20px 64px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '48px', flexWrap: 'wrap', background: COLORS.ink, color: COLORS.cream, fontSize: '14px', fontWeight: 500, textAlign: 'center' }}>
            {trustBadges.map((b, i) => (
              <div key={i}>{b.icon} {b.title} — {b.description}</div>
            ))}
          </div>
        )}

        {/* OFFER: AI-ENGINEER FOLLOW-ON COURSE + BRIDGE COURSE */}
        <div style={styles.section({ background: COLORS.orangeTint, gap: '32px', padding: '48px 64px' })}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', textAlign: 'center' }}>
            <div style={styles.eyebrow(COLORS.orangeDark)}>Bonus offer</div>
            <h3 style={{ ...styles.h2, fontSize: '24px', maxWidth: '640px' }}>{OFFER.title}</h3>
            <p style={{ margin: 0, maxWidth: '600px', fontSize: '15px', lineHeight: 1.6, color: COLORS.textBody }}>
              {OFFER.description}
            </p>
            <a
              href={OFFER.href}
              className="tf-cta-ghost"
              style={{ marginTop: '4px', border: `1.5px solid ${COLORS.orangeDark}`, color: COLORS.orangeDark, padding: '12px 24px', borderRadius: '10px', fontSize: '14.5px', fontWeight: 700 }}
            >
              {OFFER.cta}
            </a>
          </div>

          <div style={{ width: '100%', maxWidth: '640px', margin: '0 auto', background: '#FFFFFF', border: `1px solid rgba(217,119,46,0.3)`, borderRadius: '16px', padding: '28px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '10px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#2E7D5B', background: '#E6F4EC', borderRadius: '999px', padding: '4px 12px' }}>
              Free with enrollment
            </div>
            <div style={{ fontFamily: "'Merriweather', serif", fontSize: '20px', fontWeight: 700 }}>Bridge Course (Self-Learn)</div>
            <p style={{ margin: 0, fontSize: '14.5px', lineHeight: 1.6, color: COLORS.textBody, maxWidth: '520px' }}>
              Full Cycle Pro teaches you to build and ship with an AI agent — it doesn't teach LLM fundamentals, RAG, or AI-agent architecture on their own. See exactly what's covered, what isn't, and get a free self-paced bridge track for the gaps when you enroll.
            </p>
            <button
              onClick={bridgeModal.onOpen}
              style={{ marginTop: '6px', background: 'transparent', border: `1.5px solid ${COLORS.ink}`, color: COLORS.ink, borderRadius: '10px', padding: '11px 22px', fontSize: '14.5px', fontWeight: 700, cursor: 'pointer' }}
            >
              See what's covered
            </button>
          </div>
        </div>

        {/* STANDARD PRICING / ENROLL BLOCK */}
        <div id="pricing" style={{ width: '100%' }}>
          {loading ? (
            <Center py={20} bg="gray.50">
              <VStack spacing={4}>
                <Spinner size="lg" color="brand.600" />
                <Text color="gray.500" fontSize="sm">Loading pricing…</Text>
              </VStack>
            </Center>
          ) : (
            <CoursePricing data={pricing} startDate={startDate} courseId={courseId} />
          )}
        </div>

        {/* FINAL CTA */}
        <div style={styles.section({ background: COLORS.ink, color: COLORS.cream, gap: '22px', textAlign: 'center' })}>
          <h2 style={{ ...styles.h2, maxWidth: '640px', fontSize: '36px', color: COLORS.cream }}>
            Five weeks from now, you could have a shipped product and a certificate to back it.
          </h2>
          <p style={{ margin: 0, fontSize: '15px', color: 'rgba(250,248,243,0.75)' }}>
            {settings?.maxStudents ? `${settings.maxStudents} seats per cohort, kept small so every project gets real 1:1 review.` : 'Seats are kept small so every project gets real 1:1 review.'}
          </p>
          <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToPricing(); }} className="tf-cta" style={{ background: COLORS.orange, color: COLORS.cream, padding: '18px 40px', borderRadius: '10px', fontSize: '17px', fontWeight: 700, marginTop: '8px' }}>
            Reserve Your Seat
          </a>
        </div>

      </div>

      {/* TOOLS STACK MODAL */}
      <Modal isOpen={stackModal.isOpen} onClose={stackModal.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>What tools you will work on</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={6}>
              {TRANSFORM_STACK_GROUPS.map((group) => (
                <Box key={group}>
                  <Heading as="h4" size="sm" mb={3} color="gray.700">{group}</Heading>
                  <SimpleGrid columns={{ base: 3, sm: 4, md: 6 }} spacing={4}>
                    {TRANSFORM_STACK.filter((s) => s.group === group).map((s) => (
                      <VStack key={s.name} spacing={2} p={2} borderRadius="md" _hover={{ bg: 'gray.50' }}>
                        <Image src={s.logo_url} alt={s.name} boxSize="32px" borderRadius="md" fallbackSrc="https://via.placeholder.com/32" />
                        <Text fontSize="xs" textAlign="center" color="gray.700">{s.name}</Text>
                      </VStack>
                    ))}
                  </SimpleGrid>
                </Box>
              ))}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* BRIDGE COURSE MODAL */}
      <Modal isOpen={bridgeModal.isOpen} onClose={bridgeModal.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Bridge Course (Self-Learn)
            <Badge ml={3} colorScheme="green" fontSize="0.7em" verticalAlign="middle">Free with enrollment</Badge>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text fontSize="sm" color="gray.600" mb={5}>
              Full Cycle Pro is built around shipping a real project with an AI agent — that's the "full lifecycle" part of the job.
              It doesn't dedicate class time to AI-Engineer-specific fundamentals like LLMs, RAG, or agent architecture on their own.
              This self-paced Bridge Course closes that gap, and it's <b>included free</b> the moment you enroll in Full Cycle Pro.
            </Text>
            <VStack align="stretch" spacing={5}>
              {BRIDGE_TOPICS.map((row) => (
                <Box key={row.topic} pb={4} borderBottom="1px solid" borderColor="gray.100">
                  <Heading as="h4" size="sm" mb={1.5} color="gray.900">{row.topic}</Heading>
                  <Text fontSize="sm" color="gray.600" lineHeight={1.6}>{row.significance}</Text>
                </Box>
              ))}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* COPY PROMPT / HELP MODAL */}
      <Modal isOpen={promptModal.isOpen} onClose={() => setPromptModal({ ...promptModal, isOpen: false })} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{promptModal.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box bg="gray.50" borderRadius="md" p={3} mb={4}>
              <Text fontSize="sm" fontWeight="700" color="gray.800" mb={1}>How to use this</Text>
              <Text fontSize="sm" color="gray.600">
                1. Click "Copy Prompt" below to copy the text.<br />
                2. Paste it into ChatGPT, Claude, or any AI assistant you already trust.<br />
                3. Ask it to weigh in honestly — you're not just getting our pitch, you're getting a second, independent opinion on whether this actually fits your goals.
              </Text>
            </Box>
            <Textarea value={promptModal.prompt} readOnly rows={14} fontSize="sm" />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="brand" onClick={copyPrompt}>Copy Prompt</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TransformLanding;
